import { match, type StrictExtract, type StrictOmit } from '@frontend/duck-tape';
import { applyClass, concatClasses, safeCss, type ClassName } from '@frontend/web-utils';
import type { ButtonProps } from 'react-html-props';
import type { AccessibilityLabelProps } from '../../../types/accessibility';
import type { IconName } from '../Icon/helpers';
import type { IconProps } from '../Icon/Icon';
import { Icon } from '../Icon/Icon';
import { Spinner } from '../Spinner';
import type { TextProps } from '../Texts/Text';
import { Text } from '../Texts/Text';

export type LinkButtonProps = AccessibilityLabelProps &
  StrictOmit<TextProps, 'children' | 'className' | 'classNames' | 'color' | 'onClick'> & {
    className?: ClassName;
    color?: StrictExtract<TextProps['color'], 'danger' | 'primary' | 'secondary' | 'success' | 'tertiary' | 'warning'>;
    disabled?: boolean;
    hoverEffect?: 'background' | 'opacity';
    iconColor?: IconProps['color'];
    isLoading?: boolean;
    leftIconName?: IconName;
    leftIconProps?: StrictOmit<IconProps, 'name'>;
    onClick: ButtonProps['onClick'];
    rightIconName?: IconName;
    rightIconProps?: StrictOmit<IconProps, 'name'>;
    textClassName?: ClassName;
    withUnderline?: boolean;
  } & ({ children: React.ReactNode } | { label: string });

export const LinkButton = ({
  accessibilityLabel,
  className,
  color = 'primary',
  disabled = false,
  iconColor = color,
  isLoading,
  leftIconName,
  leftIconProps,
  onClick,
  rightIconName,
  rightIconProps,
  textClassName,
  withUnderline,
  hoverEffect = 'opacity',
  ...props
}: LinkButtonProps) => {
  return (
    <button
      aria-label={accessibilityLabel || ('label' in props ? props.label : '')}
      className={concatClasses(
        'flex flex-row items-center gap-x-xs justify-center',
        getHoverEffectClassName({ hoverEffect }),
        applyClass(!!onClick, 'hover:cursor-pointer'),
        className,
      )}
      disabled={disabled}
      onClick={
        disabled
          ? undefined
          : (e) => {
              e.stopPropagation();
              e.preventDefault();
              onClick?.(e);
            }
      }
    >
      {isLoading ? (
        <Spinner size="xs" />
      ) : (
        <>
          {leftIconName ? <Icon color={iconColor} name={leftIconName} {...leftIconProps} /> : null}
          <Text
            children={'children' in props ? props.children : props.label}
            className={concatClasses(
              'font-bold',
              applyClass(withUnderline, 'underline underline-offset-4'),
              textClassName,
            )}
            color={color}
            {...props}
          />
          {rightIconName ? <Icon color={iconColor} name={rightIconName} {...rightIconProps} /> : null}
        </>
      )}
    </button>
  );
};

const getHoverEffectClassName = ({ hoverEffect }: PickRequired<LinkButtonProps, 'hoverEffect'>) =>
  match(hoverEffect)
    .with('background', () => safeCss('hover-background-effect'))
    .with('opacity', () => safeCss('hover-opacity-effect'))
    .exhaustive();
